import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import clsx from "clsx";
import { toast } from "react-toastify";

import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import SmsIcon from "@material-ui/icons/SmsOutlined";
import CallIcon from "@material-ui/icons/PhoneOutlined";
import CheckIcon from "@material-ui/icons/CheckCircle";
import SendIcon from "@material-ui/icons/SendOutlined";
import RefreshIcon from "@material-ui/icons/RefreshOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";

const T = {
  bg: "#F6F7F9",
  panel: "#FFFFFF",
  line: "#E6E9ED",
  lineSoft: "#EEF0F3",
  hover: "#FAFBFC",
  ink: "#1B222A",
  ink2: "#5B6470",
  ink3: "#929AA4",
  char: "#2C353D",
  ok: "#1E9E62",
  warn: "#C77A12",
  danger: "#C0453B",
  activeBg: "#EFF8D6",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};

// exibe +5511999998888 como +55 11 99999-8888 (BR) — fallback: agrupa dígitos
const fmtPhone = (e164 = "") => {
  const s = String(e164);
  const m = s.match(/^\+55(\d{2})(\d{4,5})(\d{4})$/);
  if (m) return `+55 ${m[1]} ${m[2]}-${m[3]}`;
  return s;
};

const useStyles = makeStyles(theme => ({
  main: { flex: 1, minWidth: 0, overflowY: "auto", background: T.bg, height: "100%" },
  wrap: { width: "100%", maxWidth: 820, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { marginBottom: theme.spacing(3) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: T.ink, margin: 0 },
  subtitle: { fontSize: 13, color: T.ink2, marginTop: 4 },

  card: {
    background: T.panel,
    border: `1px solid ${T.line}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
  },
  cardHead: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2) },
  cardTitle: { fontSize: 15, fontWeight: 600, color: T.ink, margin: 0 },
  cardDesc: { fontSize: 12.5, color: T.ink2, marginTop: 3, lineHeight: 1.45, maxWidth: 560 },
  counter: { marginLeft: "auto", fontFamily: T.mono, fontSize: 12, color: T.ink3, whiteSpace: "nowrap", paddingTop: 2 },
  grow: { flex: 1, minWidth: 0 },

  // perfil
  field: { display: "flex", gap: 14, flexWrap: "wrap" },
  fieldItem: { flex: "1 1 220px", minWidth: 0 },
  fieldLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: T.ink3, fontWeight: 600, marginBottom: 5 },
  fieldValue: { fontSize: 14, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fieldValueRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  linkBtn: {
    fontSize: 12.5, fontWeight: 600, textTransform: "none", color: T.ink2, padding: "2px 8px",
    borderRadius: 6, minWidth: 0, flexShrink: 0,
    "&:hover": { background: T.hover, color: T.ink },
  },

  // lista de números
  list: { display: "flex", flexDirection: "column", gap: 8 },
  numRow: {
    display: "flex", alignItems: "center", gap: 12,
    border: `1px solid ${T.line}`, borderRadius: 12, padding: "11px 14px", background: T.panel,
  },
  numIcon: {
    width: 34, height: 34, borderRadius: 9, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: T.lineSoft, color: T.ink2, "& svg": { fontSize: 18 },
  },
  numMain: { minWidth: 0, flex: 1 },
  numE164: { fontSize: 14, fontWeight: 600, color: T.ink, fontFamily: T.mono },
  numMeta: { fontSize: 12, color: T.ink3, marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600,
    padding: "2px 8px", borderRadius: 999, "& svg": { fontSize: 13 },
  },
  chipOk: { color: T.ok, background: "#E7F5EE" },
  chipPend: { color: T.warn, background: "#FBF1E0" },
  scopeBox: { display: "flex", alignItems: "center", gap: 4, marginRight: 4 },
  scopeLabel: { fontSize: 12, color: T.ink2 },

  emptyNums: { fontSize: 13, color: T.ink3, padding: theme.spacing(1, 0.5, 2) },
  addBtn: {
    marginTop: theme.spacing(1.5), background: T.char, color: "#fff", fontSize: 12.5, fontWeight: 500,
    textTransform: "none", padding: "7px 14px", borderRadius: 8, boxShadow: "none",
    "&:hover": { background: "#1f262c", boxShadow: "none" },
    "&.Mui-disabled": { background: T.line, color: T.ink3 },
  },

  // modal
  dlgWrap: { padding: theme.spacing(3) },
  dlgTitle: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 17, color: T.ink, margin: 0 },
  dlgDesc: { fontSize: 12.5, color: T.ink2, marginTop: 5, marginBottom: theme.spacing(2.5), lineHeight: 1.45 },
  lbl: { fontSize: 11.5, fontWeight: 600, color: T.ink2, marginBottom: 6, marginTop: theme.spacing(2) },
  channelRow: { display: "flex", gap: 10 },
  channelBtn: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    border: `1px solid ${T.line}`, borderRadius: 10, padding: "10px 12px", cursor: "pointer",
    fontSize: 13, fontWeight: 500, color: T.ink2, background: T.panel, transition: "all .12s",
    "& svg": { fontSize: 18 },
    "&:hover": { borderColor: T.ink3 },
  },
  channelOn: { borderColor: T.ink, color: T.ink, background: T.hover, boxShadow: `inset 0 0 0 1px ${T.ink}` },
  otpInput: {
    "& input": { fontFamily: T.mono, fontSize: 26, letterSpacing: "0.32em", textAlign: "center", fontWeight: 600 },
  },
  dlgActions: { display: "flex", alignItems: "center", gap: 10, marginTop: theme.spacing(3) },
  primary: {
    background: T.char, color: "#fff", fontSize: 13, fontWeight: 500, textTransform: "none",
    padding: "8px 18px", borderRadius: 8, boxShadow: "none",
    "&:hover": { background: "#1f262c", boxShadow: "none" }, "&.Mui-disabled": { background: T.line, color: T.ink3 },
  },
  ghost: {
    fontSize: 13, fontWeight: 500, textTransform: "none", color: T.ink2, padding: "8px 12px", borderRadius: 8,
    "&:hover": { background: T.hover }, "&.Mui-disabled": { color: T.ink3 },
  },
  resend: {
    marginLeft: "auto", fontSize: 12.5, color: T.ink2, textTransform: "none", fontWeight: 500,
    "&:hover": { background: "transparent", color: T.ink }, "&.Mui-disabled": { color: T.ink3 },
  },
}));

const Settings = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const { confirm } = useConfirm();
  const { user, setUser } = useContext(AuthContext);

  const [numbers, setNumbers] = useState([]);
  const [limit, setLimit] = useState(3);
  const [loading, setLoading] = useState(true);

  // menu de contexto por linha
  const [rowMenu, setRowMenu] = useState({ anchor: null, num: null });

  // modal de adicionar/verificar
  const [dlg, setDlg] = useState(false);
  const [phase, setPhase] = useState("request"); // request | verify
  const [form, setForm] = useState({ e164: "", label: "", channel: "sms" });
  const [otpId, setOtpId] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  // modal de troca de e-mail (mesmo esquema: novo e-mail → código → confirma)
  const [emailDlg, setEmailDlg] = useState(false);
  const [emailPhase, setEmailPhase] = useState("request"); // request | verify
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");

  // teto de gastos (Etapa 4)
  const [spend, setSpend] = useState(null); // { capCents, spentCents, remainingCents, reached }
  const [capInput, setCapInput] = useState(""); // valor em reais no input
  const [capBusy, setCapBusy] = useState(false);
  const loadSpend = useCallback(async () => {
    try {
      const { data } = await api.get("/me/plan");
      const s = data && data.spend;
      setSpend(s || null);
      setCapInput(s && s.capCents != null ? String((s.capCents / 100).toFixed(2)) : "");
    } catch (e) {
      /* silencioso */
    }
  }, []);
  useEffect(() => { loadSpend(); }, [loadSpend]);

  const saveCap = async () => {
    setCapBusy(true);
    try {
      const v = String(capInput).trim().replace(",", ".");
      const cents = v === "" ? null : Math.round(parseFloat(v) * 100);
      const { data } = await api.put("/me/spend-cap", { cents });
      setSpend(data);
      setCapInput(data.capCents != null ? String((data.capCents / 100).toFixed(2)) : "");
      toast.success(data.capCents != null ? "Teto de gastos salvo." : "Teto de gastos removido.");
    } catch (err) {
      toastError(err);
    } finally {
      setCapBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/verified-numbers");
      setNumbers(data.numbers || []);
      if (data.limit) setLimit(data.limit);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // countdown de reenvio
  const startCooldown = useCallback(secs => {
    setResendIn(secs);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn(s => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  const openAdd = () => {
    setForm({ e164: "", label: "", channel: "sms" });
    setCode("");
    setOtpId(null);
    setPhase("request");
    setResendIn(0);
    setDlg(true);
  };

  const closeDlg = () => {
    if (busy) return;
    setDlg(false);
  };

  const requestOtp = async () => {
    if (!form.e164.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/verified-numbers/request-otp", {
        e164: form.e164.trim(),
        channel: form.channel,
        label: form.label.trim() || undefined,
      });
      setOtpId(data.id);
      setPhase("verify");
      setCode("");
      startCooldown(data.resendIn || 60);
      toast.success(
        form.channel === "call"
          ? "Vamos ligar para o seu número com o código."
          : "Enviamos um SMS com o código."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    if (resendIn > 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/verified-numbers/request-otp", {
        e164: form.e164.trim(),
        channel: form.channel,
        label: form.label.trim() || undefined,
      });
      setOtpId(data.id);
      startCooldown(data.resendIn || 60);
      toast.success("Código reenviado.");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (code.trim().length < 4 || !otpId) return;
    setBusy(true);
    try {
      await api.post(`/verified-numbers/${otpId}/verify`, { code: code.trim() });
      toast.success("Número verificado!");
      setDlg(false);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleScope = async num => {
    const next = num.scope === "account" ? "user" : "account";
    // otimista
    setNumbers(ns => ns.map(n => (n.id === num.id ? { ...n, scope: next } : n)));
    try {
      await api.put(`/verified-numbers/${num.id}`, { scope: next });
    } catch (err) {
      setNumbers(ns => ns.map(n => (n.id === num.id ? { ...n, scope: num.scope } : n)));
      toastError(err);
    }
  };

  const removeNum = async num => {
    setRowMenu({ anchor: null, num: null });
    const ok = await confirm({
      title: "Remover número",
      message: `Remover ${fmtPhone(num.e164)}? Você poderá adicioná-lo de novo depois.`,
      confirmText: "Remover",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/verified-numbers/${num.id}`);
      setNumbers(ns => ns.filter(n => n.id !== num.id));
    } catch (err) {
      toastError(err);
    }
  };

  const resumeVerify = num => {
    setRowMenu({ anchor: null, num: null });
    setForm({ e164: num.e164, label: num.label || "", channel: num.channel || "sms" });
    setOtpId(num.id);
    setCode("");
    setPhase("verify");
    setResendIn(0);
    setDlg(true);
  };

  // --- troca de e-mail ---
  const openEmail = () => {
    setNewEmail("");
    setEmailCode("");
    setEmailPhase("request");
    setResendIn(0);
    setEmailDlg(true);
  };

  const closeEmail = () => {
    if (busy) return;
    setEmailDlg(false);
  };

  const requestEmailOtp = async (isResend = false) => {
    const target = newEmail.trim();
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post("/user/email/request-otp", { email: target });
      setEmailPhase("verify");
      if (!isResend) setEmailCode("");
      startCooldown(data.resendIn || 60);
      toast.success(isResend ? "Código reenviado." : "Enviamos um código para o novo e-mail.");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (emailCode.trim().length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/user/email/verify", { code: emailCode.trim() });
      if (data.user && setUser) setUser(data.user);
      toast.success("E-mail atualizado!");
      setEmailDlg(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const fullName = (user && user.name) || "—";
  const email = (user && user.email) || "—";
  const atLimit = numbers.length >= limit;

  return (
    <div className={classes.main}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <h1 className={classes.title}>Configurações</h1>
          <div className={classes.subtitle}>Gerencie sua conta e os números usados nos testes.</div>
        </div>

        {/* Perfil */}
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <div className={classes.grow}>
              <h2 className={classes.cardTitle}>Perfil</h2>
              <div className={classes.cardDesc}>Seus dados de acesso.</div>
            </div>
          </div>
          <div className={classes.field}>
            <div className={classes.fieldItem}>
              <div className={classes.fieldLabel}>Nome</div>
              <div className={classes.fieldValue}>{fullName}</div>
            </div>
            <div className={classes.fieldItem}>
              <div className={classes.fieldLabel}>E-mail</div>
              <div className={classes.fieldValueRow}>
                <span className={classes.fieldValue}>{email}</span>
                <Button className={classes.linkBtn} onClick={openEmail}>
                  Alterar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Teto de gastos */}
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <div className={classes.grow}>
              <h2 className={classes.cardTitle}>Teto de gastos</h2>
              <div className={classes.cardDesc}>
                Limite global de excedente por mês (IA, execuções, agente, WhatsApp…). Ao atingir,
                novos consumos de crédito são bloqueados — mesmo com saldo. Deixe vazio para não ter teto.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <TextField
              size="small"
              variant="outlined"
              label="Teto por mês (R$)"
              placeholder="sem teto"
              value={capInput}
              onChange={e => setCapInput(e.target.value.replace(/[^\d.,]/g, ""))}
              style={{ width: 180 }}
              InputProps={{ startAdornment: <span style={{ color: "#929AA4", marginRight: 6, fontSize: 13 }}>R$</span> }}
            />
            <Button className={classes.primary} onClick={saveCap} disabled={capBusy}>
              {capBusy ? "Salvando…" : "Salvar"}
            </Button>
            {spend && (
              <span style={{ fontSize: 12.5, color: T.ink2 }}>
                Gasto no ciclo: <b>R$ {((spend.spentCents || 0) / 100).toFixed(2).replace(".", ",")}</b>
                {spend.capCents != null && ` de R$ ${(spend.capCents / 100).toFixed(2).replace(".", ",")}`}
                {spend.reached && <span style={{ color: T.danger, fontWeight: 600 }}> · teto atingido</span>}
              </span>
            )}
          </div>
        </div>

        {/* Telefone verificado */}
        <div className={classes.card}>
          <div className={classes.cardHead}>
            <div className={classes.grow}>
              <h2 className={classes.cardTitle}>Telefone verificado</h2>
              <div className={classes.cardDesc}>
                Confirme a posse de um número por SMS ou ligação para usá-lo com o número de
                demonstração — só é possível ligar/receber de números verificados. Você pode
                liberar um número para toda a conta (destino de teste e Caller ID).
              </div>
            </div>
            <div className={classes.counter}>{numbers.length}/{limit}</div>
          </div>

          {loading ? (
            <div className={classes.emptyNums}>Carregando…</div>
          ) : numbers.length === 0 ? (
            <div className={classes.emptyNums}>Nenhum número ainda. Adicione o seu para testar os fluxos.</div>
          ) : (
            <div className={classes.list}>
              {numbers.map(num => (
                <div key={num.id} className={classes.numRow}>
                  <div className={classes.numIcon}>
                    {num.channel === "call" ? <CallIcon /> : <SmsIcon />}
                  </div>
                  <div className={classes.numMain}>
                    <div className={classes.numE164}>{fmtPhone(num.e164)}</div>
                    <div className={classes.numMeta}>
                      {num.label && <span>{num.label}</span>}
                      {num.verified ? (
                        <span className={clsx(classes.chip, classes.chipOk)}>
                          <CheckIcon /> Verificado
                        </span>
                      ) : (
                        <span className={clsx(classes.chip, classes.chipPend)}>Pendente</span>
                      )}
                    </div>
                  </div>

                  {num.verified && (
                    <div className={classes.scopeBox}>
                      <Switch
                        size="small"
                        color="primary"
                        checked={num.scope === "account"}
                        onChange={() => toggleScope(num)}
                      />
                      <span className={classes.scopeLabel}>Liberar p/ conta</span>
                    </div>
                  )}

                  <IconButton
                    size="small"
                    onClick={e => setRowMenu({ anchor: e.currentTarget, num })}
                  >
                    <MoreVertIcon fontSize="small" style={{ color: T.ink3 }} />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          <Button
            className={classes.addBtn}
            startIcon={<AddIcon />}
            disableElevation
            variant="contained"
            disabled={atLimit}
            onClick={openAdd}
          >
            Adicionar número
          </Button>
          {atLimit && (
            <span style={{ fontSize: 12, color: T.ink3, marginLeft: 12 }}>
              Limite de {limit} atingido — remova um para adicionar outro.
            </span>
          )}
        </div>
      </div>

      {/* menu de contexto da linha */}
      <Menu
        anchorEl={rowMenu.anchor}
        open={!!rowMenu.anchor}
        onClose={() => setRowMenu({ anchor: null, num: null })}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        {rowMenu.num && !rowMenu.num.verified && (
          <MenuItem className={menu.item} onClick={() => resumeVerify(rowMenu.num)}>
            <SendIcon /> Verificar agora
          </MenuItem>
        )}
        <MenuItem
          className={clsx(menu.item, menu.danger)}
          onClick={() => removeNum(rowMenu.num)}
        >
          <DeleteIcon /> Remover
        </MenuItem>
      </Menu>

      {/* modal adicionar / verificar */}
      <Dialog
        open={dlg}
        onClose={closeDlg}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: menu.dialogPaper }}
      >
        <div className={classes.dlgWrap}>
          {phase === "request" ? (
            <>
              <h3 className={classes.dlgTitle}>Adicionar número</h3>
              <div className={classes.dlgDesc}>
                Informe o número com DDD. Enviaremos um código para confirmar que é seu.
              </div>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Número (com DDD)"
                placeholder="(11) 99999-8888"
                value={form.e164}
                onChange={e => {
                  const v = e.target.value;
                  setForm(f => ({ ...f, e164: v }));
                }}
                autoFocus
              />
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Rótulo (opcional)"
                placeholder="Meu celular"
                value={form.label}
                onChange={e => {
                  const v = e.target.value;
                  setForm(f => ({ ...f, label: v }));
                }}
                style={{ marginTop: 12 }}
              />

              <div className={classes.lbl}>Como você quer receber o código?</div>
              <div className={classes.channelRow}>
                <div
                  className={clsx(classes.channelBtn, form.channel === "sms" && classes.channelOn)}
                  onClick={() => setForm(f => ({ ...f, channel: "sms" }))}
                >
                  <SmsIcon /> SMS
                </div>
                <div
                  className={clsx(classes.channelBtn, form.channel === "call" && classes.channelOn)}
                  onClick={() => setForm(f => ({ ...f, channel: "call" }))}
                >
                  <CallIcon /> Ligação
                </div>
              </div>

              <div className={classes.dlgActions}>
                <Button className={classes.ghost} onClick={closeDlg} disabled={busy}>
                  Cancelar
                </Button>
                <div style={{ flex: 1 }} />
                <Button
                  className={classes.primary}
                  onClick={requestOtp}
                  disabled={busy || !form.e164.trim()}
                >
                  {busy ? "Enviando…" : "Enviar código"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className={classes.dlgTitle}>Confirme o código</h3>
              <div className={classes.dlgDesc}>
                {form.channel === "call" ? "Ligamos para " : "Enviamos um SMS para "}
                <b>{fmtPhone(form.e164)}</b>. Digite o código de 6 dígitos.
              </div>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                className={classes.otpInput}
                placeholder="••••••"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                autoFocus
                onKeyDown={e => e.key === "Enter" && verifyOtp()}
              />

              <div className={classes.dlgActions}>
                <Button className={classes.ghost} onClick={() => setPhase("request")} disabled={busy}>
                  Voltar
                </Button>
                <Button
                  className={classes.resend}
                  startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
                  onClick={resendOtp}
                  disabled={busy || resendIn > 0}
                >
                  {resendIn > 0 ? `Reenviar em ${resendIn}s` : "Reenviar código"}
                </Button>
                <Button
                  className={classes.primary}
                  onClick={verifyOtp}
                  disabled={busy || code.trim().length < 4}
                >
                  {busy ? "Verificando…" : "Verificar"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* modal troca de e-mail */}
      <Dialog
        open={emailDlg}
        onClose={closeEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: menu.dialogPaper }}
      >
        <div className={classes.dlgWrap}>
          {emailPhase === "request" ? (
            <>
              <h3 className={classes.dlgTitle}>Alterar e-mail</h3>
              <div className={classes.dlgDesc}>
                Informe o novo e-mail. Enviaremos um código para ele — a troca só é efetivada
                após você confirmar o código.
              </div>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Novo e-mail"
                placeholder="voce@empresa.com"
                type="email"
                value={newEmail}
                onChange={e => {
                  const v = e.target.value;
                  setNewEmail(v);
                }}
                autoFocus
                onKeyDown={e => e.key === "Enter" && requestEmailOtp(false)}
              />

              <div className={classes.dlgActions}>
                <Button className={classes.ghost} onClick={closeEmail} disabled={busy}>
                  Cancelar
                </Button>
                <div style={{ flex: 1 }} />
                <Button
                  className={classes.primary}
                  onClick={() => requestEmailOtp(false)}
                  disabled={busy || !newEmail.trim()}
                >
                  {busy ? "Enviando…" : "Enviar código"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className={classes.dlgTitle}>Confirme o código</h3>
              <div className={classes.dlgDesc}>
                Enviamos um código para <b>{newEmail.trim()}</b>. Digite o código de 6 dígitos
                para efetivar a troca.
              </div>

              <TextField
                fullWidth
                size="small"
                variant="outlined"
                className={classes.otpInput}
                placeholder="••••••"
                value={emailCode}
                onChange={e => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                autoFocus
                onKeyDown={e => e.key === "Enter" && verifyEmailOtp()}
              />

              <div className={classes.dlgActions}>
                <Button className={classes.ghost} onClick={() => setEmailPhase("request")} disabled={busy}>
                  Voltar
                </Button>
                <Button
                  className={classes.resend}
                  startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
                  onClick={() => requestEmailOtp(true)}
                  disabled={busy || resendIn > 0}
                >
                  {resendIn > 0 ? `Reenviar em ${resendIn}s` : "Reenviar código"}
                </Button>
                <Button
                  className={classes.primary}
                  onClick={verifyEmailOtp}
                  disabled={busy || emailCode.trim().length < 4}
                >
                  {busy ? "Confirmando…" : "Confirmar"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default Settings;
